"use client";

import { useEffect, useMemo, useState } from "react";
import {
  useRouter as useNextRouter,
  useParams,
  usePathname,
  useSearchParams,
} from "next/navigation";

import { getLanguageFromPathname } from "@stadium/helpers/routes";

import { PatchedAppRouterInstance } from "./app-router-provider";

const META_ROUTE_PARAM_KEY = "meta";

const getSerializedQuery = (searchParams) => Object.fromEntries(searchParams.entries());

const getAsPath = (pathname = "", searchParams = null) =>
  `${pathname}${searchParams?.toString() ? "?" + searchParams.toString() : ""}`;

const getRawPath = (pathname, params) => {
  if (!pathname) return "";
  const splittedRoute = pathname.split("/");

  if (typeof params === "object" && params !== null) {
    const _params = { ...params };

    let reconstructedPath = "";

    for (let i = 1; i < splittedRoute.length; i++) {
      const segment = splittedRoute[i];

      // Check if the segment matches any value in the params object
      const matchingKey = Object.keys(_params).find((key) => _params[key] === segment);

      if (matchingKey) {
        // Replace the segment with the dynamic segment
        reconstructedPath += `/[${matchingKey}]`;
        delete _params[matchingKey];
      } else {
        // Keep the original segment
        reconstructedPath += `/${segment}`;
      }
    }

    return reconstructedPath;
  } else {
    // Params object is not present
    return pathname;
  }
};

const getUrlFromUrlObject = (urlObject) => {
  const { pathname } = urlObject;
  const query = { ...urlObject.query };
  Object.keys(query).forEach((item) => (query[item] = query[item] ?? ""));
  const newPathname = pathname
    .split("/")
    .map((item) =>
      item.replace(/\[([a-zA-Z0-9]+)\]/g, (_, word) => {
        const replacedWord = query[word];
        delete query[word];
        return replacedWord;
      }),
    )
    .join("/");
  delete query[META_ROUTE_PARAM_KEY];
  const generateQuery = new URLSearchParams(query).toString();
  return `${newPathname}?${generateQuery}`;
};

const useRouter = (): PatchedAppRouterInstance => {
  const { push: nextPush, replace: nextReplace, ...rest } = useNextRouter();
  const rawPathname = usePathname();
  const searchParams = useSearchParams();
  const rawParams = useParams();

  const [pathname, setPathname] = useState(rawPathname);

  useEffect(() => {
    setTimeout(() => {
      setPathname(globalThis.location.pathname);
    }, 100);
  }, [rawPathname]);

  const params = useMemo(() => {
    const hasLang = rawParams.hasOwnProperty("lang");
    if (!hasLang) return rawParams;
    return {
      ...rawParams,
      lang: getLanguageFromPathname(rawPathname) || rawParams.lang,
    };
  }, [rawParams, rawPathname]);

  const customPush = (url, options) => {
    let nextUrl = url;
    if (typeof url === "object") {
      url.pathname = url.pathname ?? getRawPath(rawPathname, params);
      nextUrl = getUrlFromUrlObject(url);
    }
    return nextPush(nextUrl, options ?? {});
  };

  const customReplace = (url, options) => {
    let nextUrl = url;
    if (typeof url === "object") {
      url.pathname = url.pathname ?? getRawPath(rawPathname, params);
      nextUrl = getUrlFromUrlObject(url);
    }
    return nextReplace(nextUrl, options ?? {});
  };

  const searchParamsString = searchParams?.toString();

  const query = useMemo(
    () => ({ ...params, ...getSerializedQuery(searchParams) }),
    [params, searchParamsString],
  );

  return {
    ...rest,
    push: customPush,
    replace: customReplace,
    pathname: getRawPath(rawPathname, params),
    query,
    asPath: getAsPath(pathname, searchParams),
  };
};

export default useRouter;
